import { useCallback } from 'react'
import {
  useQuery,
  useMutation,
  type WatchQueryFetchPolicy,
} from '@apollo/client'

import { logError } from '@/helpers/log-error'
import { USER_SETTING_BY_KEY } from '@/graphql/queries/Users'
import { SET_USER_SETTING } from '@/graphql/mutations/User'
import { useLoggedInUser } from '@/hooks'
import { UserSettingKey } from '@/gql/graphql'
import { batchClient } from '@/components/App/client'

type Options = {
  fetchPolicy?: WatchQueryFetchPolicy
  skip?: boolean
}

const useUserSetting = <T,>(
  key: UserSettingKey,
  defaultValue?: T,
  { skip = false }: Options = {},
): [{ value: T; loading: boolean }, (value?: T) => Promise<void>] => {
  const { userId, originalUserId } = useLoggedInUser()

  const { data, loading } = useQuery(USER_SETTING_BY_KEY, {
    variables: { userId, key },
    skip: !userId || skip,
    client: batchClient,
  })

  const [setUserSettingMutation, { client }] = useMutation(SET_USER_SETTING)

  const currentValue = data?.userSettingByUserIdAndKey?.value ?? defaultValue

  const setUserSetting = useCallback(
    async (value?: T) => {
      if (!userId) {
        return
      }

      client.writeQuery({
        query: USER_SETTING_BY_KEY,
        data: {
          userSettingByUserIdAndKey: {
            id: key,
            userId,
            value,
          },
        },
        variables: {
          userId,
          key,
        },
      })

      if (originalUserId) {
        // Don't persist change to user settings when logged in as someone else
        return
      }

      try {
        await setUserSettingMutation({
          variables: {
            key,
            value,
          },
        })
      } catch (e) {
        logError(e as Error)
        // If the mutation fails, we need to reset the value in the cache
        client.writeQuery({
          query: USER_SETTING_BY_KEY,
          data: {
            userSettingByUserIdAndKey: {
              id: key,
              value: currentValue,
              userId,
            },
          },
        })
      }
    },
    [key, userId],
  )

  const value: T = !loading ? currentValue : defaultValue || null

  return [{ value, loading }, setUserSetting]
}

export default useUserSetting
